/**
 * @module common/components/project
 */
import React, { PropTypes, Component } from 'react';
import { browserHistory } from 'react-router';
import idx from 'idx';
import ParticipateForm from 'project/containers/participate_form';
import ViolationModal from 'project/containers/violation_modal';
import AwardsModal from 'project/containers/awards_modal';
import FeaturesModal from 'project/containers/features_modal';
import DeadlineModal from 'project/containers/deadline_modal';
import CloseModal from 'project/containers/close_modal';
import { LoadingIndicator } from 'common/components/preloader';
import Meta from 'project/containers/meta';
import ReservationModal from 'project/containers/reservation_modal';
import OfferNegotiationModal from 'entries/containers/offer_negotiation_modal';
import StatsModal from 'project/containers/stats_modal';
import SignNDAModal from 'project/containers/sign_nda';
import { ProjectRedirect } from 'common/server';
import { getProjectUrl, isCollaborator } from 'project/utils';

/**
 * Main project page container.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class Project extends Component {
  constructor(props) {
    super(props);

    this.redirectIfNeeded = this.redirectIfNeeded.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { projectID, clearProject } = this.props;

    if (prevProps.projectID !== projectID) {
      clearProject();
      this.fetchData();
    }
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    const { clearProject } = this.props;
    clearProject();
  }

  fetchData() {
    const {
      projectID, fetchProjectIfNeeded, fetchAttachments, fetchCustomContracts,
      fetchCategoriesIfNeeded, fetchWrapupsIfNeeded, user, fetchInvoice
    } = this.props;

    if (!projectID) {
      return;
    }

    fetchProjectIfNeeded({ projectId: projectID }).then(projectRes => {
      /* anonymous users don't have wrapups */
      if (!user.logged_in) {
        this.redirectIfNeeded(projectRes.data);
        return;
      }

      fetchWrapupsIfNeeded(projectID).then(wrapupsRes => {
        this.redirectIfNeeded(projectRes.data, wrapupsRes.data);
      });
    });

    fetchAttachments(projectID);
    fetchCustomContracts(projectID);
    fetchInvoice(projectID);
    fetchCategoriesIfNeeded();
  }

  redirectIfNeeded(project, wrapups=[]) {
    const { subcategorySlug, location, user } = this.props;
    const atProjectRoot = location.pathname.endsWith(`${project.id}/`);
    const isClientOrCollab = user.profile_data.id === project.client || isCollaborator(project);

    /* redirect to PAP if on draft status */
    if (project.status === 'draft') {
      browserHistory.push(`/start-a-project/${project.id}/review/`);

    /* redirect to first wrapup if user is at root URL, project has 'awarded' status and current
       user has wrapups in this project */
    } else if (atProjectRoot && project.status === 'awarded' && wrapups.length > 0) {
      const wrapupId = wrapups[0].award_id || wrapups[0].award.id;
      const url = getProjectUrl(project, subcategorySlug, `wrapup/${wrapupId}/`);
      browserHistory.push(url);
    /* redirect to project tour if user is at root URL and project has just been posted */
    } else if (atProjectRoot && idx(location, _ => _.state.isSuccess)) {
      browserHistory.push({
        pathname: getProjectUrl(project, subcategorySlug, 'tour/'),
        state: location.state
      });
    /* redirect to entries tab if user is at root URL, is client or collaborator and project
       has 'open' or 'closed' status */
    } else if (atProjectRoot && isClientOrCollab && ['open', 'closed'].includes(project.status)) {
      browserHistory.push(getProjectUrl(project, subcategorySlug, 'entries/'));
    }
  }

  render() {
    const {
      children, project, showingAwardsModal, showingFeaturesModal, showingStatsModal
    } = this.props;

    return (
      <div className="l-content" id="content">
        <Meta />
        { !!project.id && <ProjectRedirect /> }

        <section className="project-section">
          { project.id ? children : <LoadingIndicator /> }
        </section>

        {!!project.id && <ParticipateForm projectSlug={this.props.projectSlug} />}

        <ViolationModal />

        <DeadlineModal />

        <CloseModal />

        <ReservationModal />

        <OfferNegotiationModal />

        { !!project.id && <SignNDAModal /> }

        { showingAwardsModal && <AwardsModal /> }

        { showingFeaturesModal && <FeaturesModal /> }

        { showingStatsModal && <StatsModal /> }
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {node} children - page content to render
 * @property {object} user - user data
 * @property {number} projectID - id of the project this page is displaying
 * @property {string} projectSlug - slug of the project this page is displaying
 * @property {string} subcategorySlug - slug of this project's sub-category
 * @property {func} fetchProjectIfNeeded - action creator to fetch project if needed
 * @property {func} fetchCategoriesIfNeeded - action creator to fetch categories if needed
 * @property {func} fetchWrapupsIfNeeded - action creator to fetch wrapups if needed
 * @property {bool} showingAwardsModal - whether awards modal is being shown
 * @property {bool} showingFeaturesModal - whether features modal is being shown
 * @property {bool} showingStatsModal - whether stats modal is being shown
 */
Project.propTypes = {
  children: PropTypes.node,
  user: PropTypes.object,
  projectID: PropTypes.number,
  projectSlug: PropTypes.string,
  subcategorySlug: PropTypes.string,
  fetchProjectIfNeeded: PropTypes.func,
  fetchCategoriesIfNeeded: PropTypes.func,
  fetchWrapupsIfNeeded: PropTypes.func,
  clearProject: PropTypes.func.isRequired,
  fetchAttachments: PropTypes.func.isRequired,
  fetchCustomContracts: PropTypes.func.isRequired,
  project: PropTypes.object,
  showingAwardsModal: PropTypes.bool,
  showingFeaturesModal: PropTypes.bool,
  showingStatsModal: PropTypes.bool,
};

Project.childContextTypes = {
  showEntryFeedbackModal: PropTypes.func,
};



// WEBPACK FOOTER //
// ./project/components/project.jsx